import Style from './useTrendModal.module.scss';
import { FC, useMemo, useState } from 'react';
import { useModal } from 'src/application/hooks/useModal';
import { DialogModal } from 'src/components/dialogModal/dialogModal.component';
import { Loading } from 'uikit/suspense/Loading';
import { OverviewNavbar, OverviewNavbarItemProps } from 'uikit/fields/overview/OverviewNavbar';
import { TabItemProps, Tabs } from 'uikit/tabs/tabs';
import { AppButton } from 'uikit/buttons';
import { ReactComponent as ArrowLeftIcon } from 'assets/img/common/b_arrow_16_left.svg';
import { ReactComponent as ArrowRightIcon } from 'assets/img/common/b_arrow_16_right.svg';
import { ReactComponent as TrendDownIcon } from 'assets/img/common/trend_down_16.svg';
import { ReactComponent as TrendUpIcon } from 'assets/img/common/trend_up_16.svg';
import { ReactComponent as TrendPlaceholderImg } from 'assets/img/trendPlaceholder.svg';
import { DeviceBindingDto, IAggregatedValue, SummaryCalculatorEnum } from 'src/services/api/api-client';
import {
  GetDeviceDataSummaryDeviceDataSummaryQueryParameters,
  useGetDeviceDataSummaryQuery,
} from 'src/services/api/api-client/DeviceDataSummaryQuery';
import { enumToStringArr } from 'src/helpers/enum-helper';
import { Interval, isAfter, isBefore, subDays, subMonths } from 'date-fns';
import { useGetBarData, useGetInterval } from './hooks';
import { DateRangeEnum, PatientDataType } from './useTrendModal.t';
import {
  dateRangeToAggregationPeriod,
  convertIntervalToString,
  minWidthForDateString,
  valueMapFunction,
  getUnits,
  getBarType,
  BarType,
} from './helpers';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { AppBarChart, BarData } from 'uikit/appBarChart/AppBarChart.component';
import { DateFormats, localFormat } from 'src/helpers/date-helpers';
import clsx from 'clsx';

export const useTrendModal = (
  patient: PatientDataType,
  deviceParameter: SummaryCalculatorEnum,
  valueFormatter?: (value: number, paramKey: string) => string,
) => {
  const modal = useModal();

  return {
    open: modal.openModal,
    modal: modal.visible && (
      <TrendModal
        deviceParameter={deviceParameter}
        valueFormatter={valueFormatter}
        onClose={modal.closeModal}
        patient={patient}
      />
    ),
  };
};

const TrendModal: FC<{
  patient: PatientDataType;
  deviceParameter: SummaryCalculatorEnum;
  onClose: () => void;
  valueFormatter?: (value: number, paramKey: string) => string;
}> = ({ onClose, deviceParameter, valueFormatter, patient }) => {
  const { id, uniqueId, firstTherapyDay, lastTherapyDay, studyStartedAt, studyFinishedAt } = patient;
  const { t } = useScopedTranslation('Dashboard.DeviceSummaryTrends');
  const [dateRange, setDateRange] = useState(DateRangeEnum.Week);
  const { interval, goNext, goPrev } = useGetInterval(dateRange, lastTherapyDay);

  const fromDateToRequest = useMemo(
    () =>
      dateRange === DateRangeEnum.Year ? subMonths(new Date(interval.start), 1) : subDays(new Date(interval.start), 1),
    [dateRange, interval.start],
  );

  // TODO: Add debounceFunc
  const requestData = useMemo<GetDeviceDataSummaryDeviceDataSummaryQueryParameters>(
    () => ({
      patientId: id,
      deviceParameter: deviceParameter,
      from: fromDateToRequest,
      until: new Date(interval.end),
      aggregationPeriod: dateRangeToAggregationPeriod(dateRange),
    }),
    [id, deviceParameter, fromDateToRequest, interval.end, dateRange],
  );

  const { isInitialLoading, data: dto } = useGetDeviceDataSummaryQuery(requestData, {
    keepPreviousData: true,
  });

  return (
    <DialogModal
      bodyClassName={Style.modalBody}
      visible={true}
      onHide={onClose}
      title={t(`ModalTitle.${SummaryCalculatorEnum[deviceParameter]}`, SummaryCalculatorEnum[deviceParameter])}
      subTitle={t('ModalTitle.PatientUniqId', { uniqId: uniqueId })}
    >
      <NavBar />
      <div className={Style.tabContent}>
        <DateRangeMenu onChange={setDateRange} value={dateRange} />
        <PeriodSelector
          dateRange={dateRange}
          interval={interval}
          firstTherapyDate={firstTherapyDay ?? new Date()}
          lastTherapyDate={lastTherapyDay ?? new Date()}
          onPrev={goPrev}
          onNext={goNext}
        />
        <Loading loading={isInitialLoading} containerClassName={Style.trendContainer} testId={'trend-modal'}>
          <TrendSection
            values={dto?.data}
            deviceBindings={dto?.deviceBindings}
            interval={interval}
            dateRange={dateRange}
            valueFormatter={valueFormatter}
            deviceParameter={deviceParameter}
            studyStartedAt={studyStartedAt}
            studyFinishedAt={studyFinishedAt}
          />
        </Loading>
      </div>
    </DialogModal>
  );
};

const NavBar: FC = () => {
  const { t } = useScopedTranslation('Dashboard.DeviceSummaryTrends');

  const navbarItems = useMemo(
    () =>
      [
        {
          key: '1',
          selected: true,
          text: t('Navbar.SingleTrend'),
          onClick: () => {},
        },
      ] satisfies OverviewNavbarItemProps[],
    [t],
  );

  if (navbarItems.length < 2) return null;

  return <OverviewNavbar className={Style.navbar} items={navbarItems} />;
};

const DateRangeMenu: FC<{ onChange: (dateRange: DateRangeEnum) => void; value: DateRangeEnum }> = ({
  onChange,
  value,
}) => {
  const { t } = useScopedTranslation('Dashboard.DeviceSummaryTrends');

  const tabs = useMemo(
    () =>
      enumToStringArr(DateRangeEnum).map(
        (x: string) =>
          ({
            text: t(`DateRanges.${x.toString()}`, x.toString()),
            onClick: () => onChange(DateRangeEnum[x as keyof typeof DateRangeEnum]),
            isSelected: value === DateRangeEnum[x as keyof typeof DateRangeEnum],
          } satisfies TabItemProps),
      ),

    [onChange, t, value],
  );

  return <Tabs className={Style.interval} items={tabs} variant={'First'} />;
};

const PeriodSelector: FC<{
  dateRange: DateRangeEnum;
  interval: Interval;
  firstTherapyDate: Date;
  lastTherapyDate: Date;
  onPrev: () => void;
  onNext: () => void;
}> = ({ dateRange, interval, firstTherapyDate, lastTherapyDate, onNext, onPrev }) => {
  const dateStr = useMemo(() => convertIntervalToString(dateRange, interval), [dateRange, interval]);

  return (
    <div className={Style.periodContainer}>
      <AppButton
        variant={'icon-link'}
        colorSchema={'primary'}
        Icon={ArrowLeftIcon}
        disabled={isBefore(interval.start, firstTherapyDate)}
        onClick={onPrev}
      />
      <span style={{ minWidth: minWidthForDateString(dateRange) }}>{dateStr}</span>
      <AppButton
        variant={'icon-link'}
        colorSchema={'primary'}
        Icon={ArrowRightIcon}
        disabled={isAfter(interval.end, lastTherapyDate)}
        onClick={onNext}
      />
    </div>
  );
};

const TrendSection: FC<{
  values: IAggregatedValue[] | undefined;
  deviceBindings: DeviceBindingDto[] | undefined;
  interval: Interval;
  dateRange: DateRangeEnum;
  deviceParameter: SummaryCalculatorEnum;
  valueFormatter?: (value: number, paramKey: string) => string;
  studyStartedAt: Date | null;
  studyFinishedAt: Date | null;
}> = ({
  values,
  dateRange,
  interval,
  valueFormatter,
  deviceParameter,
  deviceBindings,
  studyStartedAt,
  studyFinishedAt,
}) => {
  const barData = useGetBarData(
    values ?? [],
    interval,
    dateRange,
    deviceBindings,
    studyStartedAt,
    studyFinishedAt,
    valueMapFunction(deviceParameter),
  );

  if (!values || !values.length) return <Placeholder />;

  return (
    <AppBarChart<IAggregatedValue | null>
      data={barData}
      units={getUnits(deviceParameter)}
      renderTooltip={(x) => (
        <Tooltip
          payload={x}
          valueFormatter={valueFormatter}
          barType={getBarType(x.source?.from, x.source, deviceBindings)}
        />
      )}
      xAxisTickStep={dateRange === DateRangeEnum.Month ? 6 : undefined}
    />
  );
};

const Placeholder: FC = () => {
  const { t } = useScopedTranslation('Dashboard.DeviceSummaryTrends');

  return (
    <div className={Style.placeholder}>
      <TrendPlaceholderImg />
      <span>{t('PlaceholderText')}</span>
    </div>
  );
};

const Tooltip: FC<{
  payload: BarData<IAggregatedValue | null> | undefined;
  barType: BarType;
  valueFormatter?: (value: number, paramKey: string) => string;
}> = ({ payload, valueFormatter, barType }) => {
  const { t } = useScopedTranslation('Dashboard.DeviceSummaryTrends');
  if (!payload || !payload.source || barType === 'OutOfStudy') return null;

  return (
    <div className={Style.tooltip}>
      <div className={Style.date}>{localFormat(payload.source.from, DateFormats.longDate)}</div>
      {barType === 'WaitingForData' ? (
        <div className={Style.waitingDataTooltip}>{t('WaitingDataText')}</div>
      ) : barType === 'NoDevice' ? (
        <div className={Style.waitingDataTooltip}>{t('NoDeviceText')}</div>
      ) : (
        <>
          <div className={Style.value}>
            {valueFormatter?.(payload.source.value ?? 0, 'value') ?? payload.source.value ?? 0}
          </div>
          <Changes changesValue={payload.changes} valueFormatter={valueFormatter} />
        </>
      )}
    </div>
  );
};

const Changes: FC<{ changesValue: number | null; valueFormatter?: (value: number, paramKey: string) => string }> = ({
  changesValue,
  valueFormatter,
}) => {
  if (changesValue === null) return null;

  const type = changesValue > 0 ? 'positive' : changesValue < 0 ? 'negative' : 'noChanges';

  return (
    <div
      className={clsx(Style.changes, {
        [Style.positive]: type === 'positive',
        [Style.negative]: type === 'negative',
      })}
    >
      {getChangesIcon(type)}
      <span>
        {changesValue > 0 ? '+' : changesValue < 0 ? '-' : ''}
        {valueFormatter?.(changesValue, 'changes')}
      </span>
    </div>
  );
};

const getChangesIcon = (type: 'positive' | 'negative' | 'noChanges') =>
  ({
    ['positive']: <TrendUpIcon />,
    ['negative']: <TrendDownIcon />,
    ['noChanges']: null,
  }[type]);
